import DOMPurify from 'dompurify';

const validReadyStates = ['complete', 'interactive'];

export const onDOMReady = fn => {
    const listener = event => {
        if (validReadyStates.includes(document.readyState)) {
            document.removeEventListener(event.type, listener, true);
            return fn();
        }
    };
    document.addEventListener('readystatechange', listener, true);
};

export const showFullscreen = elem => {
    if (!elem) {
        return;
    }
    if (elem.requestFullscreen) {
        elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
        /* Firefox */
        elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
        /* IE/Edge */
        elem.msRequestFullscreen();
    }
};

export const hideFullscreen = () => {
    if (
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.fullscreen
    ) {
        // can use exitFullscreen
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
            /* Firefox */
            document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
            /* Chrome, Safari and Opera */
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
            /* IE/Edge */
            document.msExitFullscreen();
        }
    }
};

export const handleSticky = (elem, { top = 0, bottom = 0 } = {}) => {
    if (!elem) {
        return -1;
    }
    const boundingBox = elem.getBoundingClientRect();
    if (window.innerHeight - bottom >= boundingBox.bottom) {
        return 1;
    } else if (boundingBox.top < top) {
        return 0;
    } else {
        return -1;
    }
};

export const addIdToElements = (parentElement, elementCounterObj, prefix = '') => {
    Object.keys(elementCounterObj).forEach(element => {
        const nodesToAddId = Array.from(
            parentElement.querySelectorAll(`${element}:not([data-testid])`)
        ).filter(n => !n.closest('[data-testid-ignore]'));
        nodesToAddId.forEach(node => {
            node.dataset.testid = `${prefix}_${element}_${++elementCounterObj[element]}`;
        });
    });
};

/**
 * Sanitizes HTML content using DOMPurify to prevent XSS attacks
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHtml = html => {
    if (!html || typeof html !== 'string') {
        return '';
    }

    const sanitizedHtml = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
            'a',
            'b',
            'blockquote',
            'br',
            'code',
            'del',
            'em',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'i',
            'li',
            'ol',
            'p',
            'pre',
            's',
            'span',
            'strike',
            'strong',
            'u',
            'ul',
        ],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class', 'title'],
        ALLOW_DATA_ATTR: false,
    });

    return sanitizedHtml;
};

/**
 * Removes empty HTML tags from the given HTML string
 * @param {string} html - The HTML string to remove empty tags from
 * @returns {string} - HTML string with empty tags removed
 */
export const removeEmptyHtmlTags = html => {
    const cleanedHtml = html.replace(/>\s+</g, '><').replace(/\n/g, '').trim();

    const parser = new DOMParser();

    const doc = parser.parseFromString(cleanedHtml, 'text/html');

    function isEmpty(node) {
        if (node.nodeType === Node.TEXT_NODE) {
            return !node.textContent.trim();
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
            const childNodes = Array.from(node.childNodes);
            return childNodes.every(isEmpty);
        }

        return false;
    }

    function removeEmptyNodes(node) {
        const children = Array.from(node.childNodes);

        for (const child of children) {
            removeEmptyNodes(child);

            if (child.nodeType === Node.ELEMENT_NODE && !child.hasAttributes() && isEmpty(child)) {
                node.removeChild(child);
            }
        }
    }

    removeEmptyNodes(doc.body);

    return doc.body.innerHTML;
};

/**
 * Sanitizes and cleans HTML content - combines sanitization with empty tag removal
 * @param {string} html - The HTML string to sanitize and clean
 * @returns {string} - Sanitized and cleaned HTML string
 */
export const sanitizeAndCleanHtml = html => {
    const sanitized = sanitizeHtml(html);
    return removeEmptyHtmlTags(sanitized);
};
